import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, ChevronRight, Download, Copy, Settings2, Package, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useRmaList } from '../hooks/useRma';
import { RmaStatusBadge } from '../components/RmaStatusBadge';
import type { RmaStatus, RmaListItem, RmaFilters } from '../types';
import { RMA_STATUS_LABELS, RMA_REASON_LABELS } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return '—';
  }
}

// =============================================================================
// Column Definitions
// =============================================================================
// Module-level labels are English fallbacks; the page resolves each label
// (and the select filterOptions) through t() at render time — see `columns` below.

const COLUMNS: ColumnDef<RmaListItem>[] = [
  {
    key: 'rmaNumber',
    label: 'RMA #',
    sortKey: 'rmaNumber',
    filterType: 'text',
    getValue: (r) => r.rmaNumber,
    render: (r) => <span className="font-mono font-medium text-sm">{r.rmaNumber}</span>,
  },
  {
    key: 'accountName',
    label: 'Account',
    filterType: 'text',
    getValue: (r) => r.accountName || '',
    render: (r) => r.accountName || <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'quoteNumber',
    label: 'Quote',
    filterType: 'text',
    getValue: (r) => r.quoteNumber || '',
    render: (r) =>
      r.quoteNumber ? (
        <span className="font-mono text-sm">{r.quoteNumber}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    filterType: 'select',
    filterOptions: (Object.keys(RMA_STATUS_LABELS) as RmaStatus[]).map((s) => ({
      label: RMA_STATUS_LABELS[s],
      value: s,
    })),
    getValue: (r) => r.status,
    render: (r) => <RmaStatusBadge status={r.status} />,
  },
  {
    key: 'reason',
    label: 'Reason',
    filterType: 'select',
    filterOptions: Object.entries(RMA_REASON_LABELS).map(([value, label]) => ({
      label,
      value,
    })),
    getValue: (r) => r.reason,
    render: (r) => <span className="text-sm">{RMA_REASON_LABELS[r.reason] || r.reason}</span>,
  },
  {
    key: 'itemCount',
    label: 'Items',
    sortKey: 'itemCount',
    filterType: 'number-range',
    getValue: (r) => r.itemCount,
    align: 'center',
    className: 'tabular-nums',
  },
  {
    key: 'createdAt',
    label: 'Created',
    sortKey: 'createdAt',
    filterType: 'date-range',
    getValue: (r) => r.createdAt,
    render: (r) => <span className="text-sm text-muted-foreground">{formatDate(r.createdAt)}</span>,
  },
  {
    key: 'cpTrackingPin',
    label: 'Tracking PIN',
    filterType: 'text',
    getValue: (r) => r.cpTrackingPin || '',
    render: (r) =>
      r.cpTrackingPin ? (
        <span className="font-mono text-sm">{r.cpTrackingPin}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: () => <ChevronRight className="h-4 w-4 text-gray-400" />,
    align: 'right',
  },
];

// =============================================================================
// Storage Keys
// =============================================================================

const FILTER_STORAGE_KEY = 'atek-rma-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-rma-column-visibility';

// =============================================================================
// Column Visibility Helpers
// =============================================================================

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// CSV Export Utilities
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(
  items: RmaListItem[],
  columns: ColumnDef<RmaListItem>[],
  filename: string,
): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(
  items: RmaListItem[],
  columns: ColumnDef<RmaListItem>[],
): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// =============================================================================
// SortableHeader Component
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Rendering
// =============================================================================

type TranslateFn = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

function renderColumnFilter(
  col: ColumnDef<RmaListItem>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  t: TranslateFn,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {selected.length
                ? t('rma.list.selected', '{{count}} selected', { count: selected.length })
                : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title={t('rma.list.from', 'From')}
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title={t('rma.list.to', 'To')}
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder={t('rma.list.min', 'Min')}
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder={t('rma.list.max', 'Max')}
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// Constants
// =============================================================================

const ALL_STATUSES = 'all';
const PAGE_SIZE = 25;

// =============================================================================
// RMA List Page
// =============================================================================

export function RmaListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('operations');

  // ---- Localized columns (labels + select filter options) -----------------
  const columns = useMemo(() => {
    const labelFor = (col: ColumnDef<RmaListItem>): string => {
      switch (col.key) {
        case 'rmaNumber': return t('rma.list.col.rmaNumber', col.label);
        case 'accountName': return t('rma.list.col.account', col.label);
        case 'quoteNumber': return t('rma.list.col.quote', col.label);
        case 'status': return t('rma.list.col.status', col.label);
        case 'reason': return t('rma.list.col.reason', col.label);
        case 'itemCount': return t('rma.list.col.items', col.label);
        case 'createdAt': return t('rma.list.col.created', col.label);
        case 'cpTrackingPin': return t('rma.list.col.trackingPin', col.label);
        default: return col.label;
      }
    };
    return COLUMNS.map((col) => ({
      ...col,
      label: labelFor(col),
      filterOptions: col.filterOptions?.map((opt) =>
        col.key === 'status'
          ? { ...opt, label: t(`rma.status.${opt.value}`, opt.label) }
          : col.key === 'reason'
            ? { ...opt, label: t(`rma.reason.${opt.value}`, opt.label) }
            : opt,
      ),
    }));
  }, [t]);

  // ---- Server-side filter / sort / pagination state -----------------------
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ---- Column-level filters (client-side, within current page) ------------
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => {
      try {
        const stored = localStorage.getItem(FILTER_STORAGE_KEY);
        if (stored) return JSON.parse(stored);
      } catch { /* ignore */ }
      return {};
    },
  );

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ---- Column visibility --------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    loadColumnVisibility,
  );

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = columns.filter((col) => isVisible(col.key));

  // ---- Server-side data fetch ---------------------------------------------
  const filters: RmaFilters = {
    search: debouncedSearch || undefined,
    status: statusFilter !== ALL_STATUSES ? (statusFilter as RmaStatus) : undefined,
    pageNumber: page,
    pageLength: PAGE_SIZE,
    sortKey,
    sortDirection,
  };

  const rmaQuery = useRmaList(filters);
  const items = rmaQuery.data?.items ?? [];
  const total = rmaQuery.data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  // ---- Client-side column filtering (within current page) -----------------
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return items;

    return items.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        // Date range: "from>>to"
        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        // Multi-select: JSON array
        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        // Number range: "min>>max"
        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        // Text: case-insensitive includes
        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [items, columnFilters]);

  // ---- Handlers -----------------------------------------------------------
  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handleRowClick = (id: string) => {
    navigate(`/operations/rma/${id}`);
  };

  const handleCopy = () => {
    copyToClipboard(filteredItems, columns);
    toast.success(t('rma.list.copySuccess', 'Copied to clipboard'));
  };

  const handleExportCSV = () => {
    exportToCSV(filteredItems, columns, 'rma');
    toast.success(t('rma.list.exportedCsv', 'Exported {{count}} rows', { count: filteredItems.length }));
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">{t('rma.list.title', 'Return Authorizations')}</h1>
        </div>
        <Button onClick={() => navigate('/operations/rma/new')}>
          <Plus className="h-4 w-4 mr-1" />
          {t('rma.list.new', 'New RMA')}
        </Button>
      </div>

      {/* Server-side Filters Row */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder={t('rma.list.searchPlaceholder', 'Search by RMA #, account, tracking...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm h-8"
        />
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px] h-8">
            <SelectValue placeholder={t('rma.list.allStatuses', 'All Statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('rma.list.allStatuses', 'All Statuses')}</SelectItem>
            {(Object.keys(RMA_STATUS_LABELS) as RmaStatus[]).map((status) => (
              <SelectItem key={status} value={status}>
                {t(`rma.status.${status}`, RMA_STATUS_LABELS[status])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground ml-auto">
          {t(total === 1 ? 'rma.list.result_one' : 'rma.list.result_other', '{{count}} results', { count: total })}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>
              {filteredItems.length < items.length
                ? t('rma.list.showingPage', 'Showing {{shown}} of {{onPage}} on this page', {
                    shown: filteredItems.length,
                    onPage: items.length,
                  })
                : t('rma.list.showingTotal', 'Showing {{shown}} of {{total}}', {
                    shown: items.length,
                    total,
                  })}
            </CardDescription>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-1" /> {t('rma.list.copy', 'Copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-1" /> {t('rma.list.csv', 'CSV')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings2 className="h-4 w-4 mr-1" /> {t('rma.list.columns', 'Columns')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{t('rma.list.toggleColumns', 'Toggle columns')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns.filter((c) => !c.noFilter).map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={isVisible(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* ROW 1: Column filter inputs (always visible) */}
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  {visibleColumns.map((col) =>
                    renderColumnFilter(col, columnFilters, setColumnFilter, t),
                  )}
                </TableRow>

                {/* ROW 2: Column labels + sort controls */}
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={cn(
                        col.align === 'right' && 'text-right',
                        col.align === 'center' && 'text-center',
                      )}
                    >
                      {col.sortKey ? (
                        <SortableHeader
                          label={col.label}
                          sortKey={col.sortKey}
                          currentSortKey={sortKey}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      ) : (
                        col.label
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Loading state */}
                {rmaQuery.isLoading &&
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i}>
                      {visibleColumns.map((col) => (
                        <TableCell key={col.key}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}

                {/* Error state */}
                {rmaQuery.error && (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="py-12 text-center text-red-500"
                    >
                      {t('rma.list.loadError', 'Error loading RMA requests. Please try again.')}
                    </TableCell>
                  </TableRow>
                )}

                {/* Empty state */}
                {!rmaQuery.isLoading && !rmaQuery.error && filteredItems.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="py-12 text-center"
                    >
                      <Package className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground font-medium">{t('rma.list.noResults', 'No RMA requests found')}</p>
                      <p className="text-sm text-muted-foreground/70 mt-1">
                        {debouncedSearch || statusFilter !== ALL_STATUSES
                          ? t('rma.list.noResultsFiltered', 'Try adjusting your search or filters.')
                          : t('rma.list.emptyCta', 'Create a new return authorization to get started.')}
                      </p>
                    </TableCell>
                  </TableRow>
                )}

                {/* Data rows */}
                {!rmaQuery.isLoading &&
                  filteredItems.map((rma) => (
                    <TableRow
                      key={rma.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(rma.id)}
                    >
                      {visibleColumns.map((col) => (
                        <TableCell
                          key={col.key}
                          className={cn(
                            'text-sm',
                            col.align === 'right' && 'text-right tabular-nums',
                            col.align === 'center' && 'text-center',
                            col.className,
                          )}
                        >
                          {col.render
                            ? col.render(rma)
                            : String(col.getValue(rma) ?? '') || '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('rma.list.pageOf', 'Page {{page}} of {{pages}}', { page: page + 1, pages: totalPages })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                >
                  {t('rma.list.previous', 'Previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  {t('rma.list.next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
